import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { AnimatedLogo } from '../AnimatedLogo';
import { NewBadge } from '../WhatsNewModal';
import { isFeatureNew } from '../../config/version';
import type { UserMode } from '../../hooks/useUserMode';
import { usePageVisibility, type PageVisibilityPrefs } from '../../hooks/usePageVisibility';
import { usePageModules } from '../../hooks/usePageModules';
import { useIsMobile } from '../../hooks/useIsMobile';

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  badge?: number;
  onClick?: () => void;
  expertOnly?: boolean;
  visibilityKey?: keyof PageVisibilityPrefs;
  moduleId?: string; // For module-based visibility
  children?: NavItem[]; // Sub-items for expandable menus
  isNew?: boolean; // Show "NEW" badge
}

interface SidebarProps {
  activeTab: string;
  onTabChange: (tabId: string) => void;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
  userMode?: UserMode;
}

export function Sidebar({ activeTab, onTabChange, isMobileOpen = false, onMobileClose, userMode = 'simple' }: SidebarProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [pageVisibility] = usePageVisibility();
  const { isModuleEnabled } = usePageModules();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]); // All menus closed by default
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null); // Track hovered menu for preview

  // Translation mapping for nav item labels
  const labels: Record<string, string> = {
    'module-hub': t('sidebar.moduleHub'),
    'portfolio': t('sidebar.portfolio'),
    'analytics': t('sidebar.analytics'),
    'analytics-overview': t('sidebar.overview'),
    'analytics-performance': t('sidebar.performance'),
    'analytics-risk': t('sidebar.risk'),
    'analytics-trading': t('sidebar.trading'),
    'analytics-positions': t('sidebar.positions'),
    'swap-analytics': t('sidebar.swapAnalytics', 'Swap Analytics'),
    'bitcoin': t('sidebar.bitcoin', 'Bitcoin'),
    'bitcoin-cycles': t('sidebar.bitcoinCycles'),
    'bitcoin-retirement': t('sidebar.bitcoinRetirement', 'Retraite Bitcoin'),
    'bot': t('sidebar.aiTradingBot'),
    'autopilot': t('sidebar.autopilot'),
    'ai-consensus': t('sidebar.aiConsensus', 'AI Consensus Bot'),
    'academy': t('sidebar.academy'),
    'crypto-quiz': t('sidebar.cryptoQuiz', 'Quiz Crypto'),
    'guide': t('sidebar.completeGuide'),
    'tax': t('sidebar.tax'),
    'tax-strategies': t('sidebar.taxGuide'),
    'tax-center': t('sidebar.taxCenter'),
    'tax-loss-harvesting': t('sidebar.taxLossHarvesting'),
    'portfolio-rebalancing': t('sidebar.portfolioRebalancing'),
    'analytics-insights': t('sidebar.analyticsInsights', 'Insights'),
    'monitor': t('sidebar.monitor', 'Monitor'),
    'fluxnode-monitor': t('sidebar.fluxnodeMonitor'),
    'hedera-monitor': t('sidebar.hederaMonitor'),
    'cronos-monitor': t('sidebar.cronosMonitor'),
    'thegraph-monitor': t('sidebar.thegraphMonitor', 'The Graph Monitor'),
    'algorand-monitor': t('sidebar.algorandMonitor', 'Algorand Monitor'),
    'cardano-monitor': t('sidebar.cardanoMonitor', 'Cardano Monitor'),
    'xdc-monitor': t('sidebar.xdcMonitor', 'XDC Monitor'),
    'vechain-monitor': t('sidebar.vechainMonitor', 'VeChain Monitor'),
    'icp-monitor': t('sidebar.icpMonitor', 'ICP Monitor'),
    'axelar-monitor': t('sidebar.axelarMonitor', 'Axelar Monitor'),
    'portfolio-onchain': t('sidebar.portfolioOnchain', 'Portfolio On-Chain ERC-20'),
    'wallet-tracker': t('sidebar.walletTracker'),
    'cryptocom-dashboard': t('sidebar.cryptocomDashboard'),
    'kucoin-dashboard': t('sidebar.kucoinDashboard'),
    'changelog': t('sidebar.changelog'),
    'projects': t('sidebar.projects'),
    'recommendations': t('sidebar.recommendations', 'Recommandations'),
    'logs': t('sidebar.logs'),
    'seed-vault': t('sidebar.seedVault'),
    'public-api': t('sidebar.publicApi', 'API Publique'),
    'support': t('sidebar.support'),
    'account': t('sidebar.account', 'Mon Compte'),
    'settings': t('sidebar.settings'),
    'pricing': t('sidebar.pricing', 'Tarifs'),
    'alerts': t('sidebar.alerts', 'Alertes Prix'),
  };
  const getNavLabel = (id: string): string => labels[id] || id;

  // Auto-expand sidebar when opened on mobile
  useEffect(() => {
    if (isMobileOpen) {
      setIsCollapsed(false);
    }
  }, [isMobileOpen]);

  // V2.I.12 : when the drawer is open on mobile, Escape closes it.
  // (The backdrop click already does that ; Escape is the keyboard/a11y
  // equivalent expected by every modal-like surface.)
  useEffect(() => {
    if (!isMobileOpen || !onMobileClose) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onMobileClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isMobileOpen, onMobileClose]);

  // Auto-expand parent menu when a child is active
  useEffect(() => {
    const analyticsIds = ['analytics-overview', 'analytics-performance', 'analytics-risk', 'analytics-trading', 'analytics-positions', 'analytics-insights', 'swap-analytics'];
    const taxIds = ['tax-strategies', 'tax-center', 'tax-loss-harvesting'];
    const monitorIds = ['fluxnode-monitor', 'hedera-monitor', 'cronos-monitor', 'thegraph-monitor', 'algorand-monitor', 'cardano-monitor', 'xdc-monitor', 'vechain-monitor', 'icp-monitor', 'axelar-monitor', 'portfolio-onchain', 'wallet-tracker', 'congress-trading'];
    const bitcoinIds = ['bitcoin-cycles', 'bitcoin-retirement'];

    if (analyticsIds.includes(activeTab) && !expandedMenus.includes('analytics')) {
      setExpandedMenus(prev => [...prev, 'analytics']);
    }
    if (taxIds.includes(activeTab) && !expandedMenus.includes('tax')) {
      setExpandedMenus(prev => [...prev, 'tax']);
    }
    if (monitorIds.includes(activeTab) && !expandedMenus.includes('monitor')) {
      setExpandedMenus(prev => [...prev, 'monitor']);
    }
    if (bitcoinIds.includes(activeTab) && !expandedMenus.includes('bitcoin')) {
      setExpandedMenus(prev => [...prev, 'bitcoin']);
    }
  }, [activeTab, expandedMenus]);

  const toggleMenu = (menuId: string) => {
    setExpandedMenus(prev =>
      prev.includes(menuId)
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const allNavItems: NavItem[] = [
    // Module Hub - First item so users understand they should start here
    {
      id: 'module-hub',
      label: 'Module Hub',
      icon: Icons.Layout,
      moduleId: 'module-hub', // Always visible
      isNew: true,
    },
    {
      id: 'portfolio',
      label: 'Portfolio',
      icon: Icons.Briefcase,
      moduleId: 'portfolio', // Always visible
    },
    // Price Alerts - requires authentication
    {
      id: 'alerts',
      label: 'Alertes Prix',
      icon: Icons.Bell,
      moduleId: 'alerts',
      isNew: true,
    },
    // New Analytics section with sub-pages
    // V2.G8: 6 child entries replaced by the AnalyticsHub. Clicking
    // "Analytics" lands on Overview ; tabs inside the hub navigate
    // between the 6 sub-pages without changing the sidebar selection.
    // Swap Analytics stays standalone (different data model, expert mode).
    {
      id: 'analytics-overview',
      label: 'Analytics',
      icon: Icons.BarChart,
      moduleId: 'analytics',
    },
    {
      id: 'swap-analytics',
      label: 'Swap Analytics',
      icon: Icons.ArrowRightLeft,
      moduleId: 'swap-analytics',
      isNew: true,
    },
    // V2.G5: Visualizations 1/2/3 deleted, charts canonicalized into analytics/*.
    // Bitcoin section with submenu
    {
      id: 'bitcoin',
      label: 'Bitcoin',
      icon: Icons.Bitcoin,
      children: [
        {
          id: 'bitcoin-cycles',
          label: 'Cycles Bitcoin',
          icon: Icons.TrendingUp,
          moduleId: 'bitcoin-cycles',
        },
        {
          id: 'bitcoin-retirement',
          label: 'Retraite Bitcoin',
          icon: Icons.Target,
          moduleId: 'bitcoin-retirement',
          isNew: true,
        },
      ],
    },
    {
      id: 'bot',
      label: 'AI Trading Bot',
      icon: Icons.Bot,
      expertOnly: true,
      moduleId: 'bot',
    },
    {
      id: 'autopilot',
      label: 'Autopilot',
      icon: Icons.Sparkles,
      expertOnly: true,
      moduleId: 'autopilot',
    },
    {
      id: 'ai-consensus',
      label: 'AI Consensus Bot',
      icon: Icons.Cpu,
      expertOnly: true,
      moduleId: 'ai-consensus',
      isNew: true,
    },
    // {
    //   id: 'academy',
    //   label: 'Academy',
    //   icon: Icons.BookOpen,
    //   moduleId: 'academy',
    // },
    {
      id: 'crypto-quiz',
      label: 'Quiz Crypto',
      icon: Icons.Brain,
      moduleId: 'crypto-quiz',
      isNew: true,
    },
    {
      id: 'guide',
      label: 'Guide Complet',
      icon: Icons.FileText,
      moduleId: 'guide',
    },
    // Tax section with submenu
    {
      id: 'tax',
      label: 'Fiscalité',
      icon: Icons.Calculator,
      children: [
        {
          id: 'tax-strategies',
          label: 'Guide Fiscal',
          icon: Icons.BookOpen,
          moduleId: 'tax-strategies',
        },
        {
          id: 'tax-center',
          label: 'Centre Fiscal',
          icon: Icons.Calculator,
          moduleId: 'tax-center',
          isNew: true,
        },
        {
          id: 'tax-loss-harvesting',
          label: 'Tax Loss Harvesting',
          icon: Icons.TrendingDown,
          moduleId: 'tax-loss-harvesting',
        },
      ],
    },
    {
      id: 'portfolio-rebalancing',
      label: 'Portfolio Rebalancing',
      icon: Icons.Target,
      expertOnly: true,
      moduleId: 'portfolio-rebalancing',
    },
    // V2.G6: AdvancedAnalytics fused into analytics/Insights (now inside the Analytics group above).
    // Monitor section with submenu
    {
      id: 'monitor',
      label: 'Monitor',
      icon: Icons.Eye,
      children: [
        {
          id: 'fluxnode-monitor',
          label: 'FluxNode Monitor',
          icon: Icons.Flux,
          moduleId: 'fluxnode-monitor',
          isNew: isFeatureNew('fluxnode-dapps'),
        },
        {
          id: 'hedera-monitor',
          label: 'Hedera Monitor',
          icon: Icons.Hedera,
          moduleId: 'hedera-monitor',
        },
        {
          id: 'cronos-monitor',
          label: 'Cronos Monitor',
          icon: Icons.Cronos,
          moduleId: 'cronos-monitor',
        },
        {
          id: 'thegraph-monitor',
          label: 'The Graph Monitor',
          icon: Icons.TheGraph,
          moduleId: 'thegraph-monitor',
          isNew: true,
        },
        {
          id: 'algorand-monitor',
          label: 'Algorand Monitor',
          icon: Icons.Algorand,
          moduleId: 'algorand-monitor',
          isNew: true,
        },
        {
          id: 'cardano-monitor',
          label: 'Cardano Monitor',
          icon: Icons.Cardano,
          moduleId: 'cardano-monitor',
          isNew: true,
        },
        {
          id: 'xdc-monitor',
          label: 'XDC Monitor',
          icon: Icons.Xdc,
          moduleId: 'xdc-monitor',
          isNew: true,
        },
        {
          id: 'vechain-monitor',
          label: 'VeChain Monitor',
          icon: Icons.Vechain,
          moduleId: 'vechain-monitor',
          isNew: true,
        },
        {
          id: 'icp-monitor',
          label: 'ICP Monitor',
          icon: Icons.Icp,
          moduleId: 'icp-monitor',
          isNew: true,
        },
        {
          id: 'axelar-monitor',
          label: 'Axelar Monitor',
          icon: Icons.Axelar,
          moduleId: 'axelar-monitor',
          isNew: true,
        },
        {
          id: 'portfolio-onchain',
          label: 'Portfolio On-Chain ERC-20',
          icon: Icons.Link,
          moduleId: 'portfolio-onchain',
          isNew: true,
        },
        {
          id: 'wallet-tracker',
          label: 'Wallet Tracker',
          icon: Icons.Wallet,
          moduleId: 'wallet-tracker',
          isNew: isFeatureNew('wallet-multi-api'),
        },
        {
          id: 'congress-trading',
          label: 'Congress Trading',
          icon: Icons.Briefcase,
          moduleId: 'congress-trading',
          isNew: true,
        },
      ],
    },
    {
      id: 'cryptocom-dashboard',
      label: 'Crypto.com Dashboard',
      icon: Icons.CryptoCom,
      visibilityKey: 'cryptocom',
      moduleId: 'cryptocom-dashboard',
    },
    {
      id: 'kucoin-dashboard',
      label: 'KuCoin Dashboard',
      icon: Icons.KuCoin,
      visibilityKey: 'kucoin',
      moduleId: 'kucoin-dashboard',
    },
    {
      id: 'changelog',
      label: 'Changelog',
      icon: Icons.FileText,
      moduleId: 'changelog',
      isNew: isFeatureNew('changelog-redesign'),
    },
    {
      id: 'logs',
      label: 'Logs',
      icon: Icons.FileText,
      expertOnly: true,
      moduleId: 'logs',
    },
    // Public API - Developer tools
    {
      id: 'public-api',
      label: 'API Publique',
      icon: Icons.Webhook,
      moduleId: 'public-api',
      isNew: true,
    },
    // SeedCrypt - Always last for security focus
    {
      id: 'seed-vault',
      label: 'SeedCrypt',
      icon: Icons.Shield,
      moduleId: 'seed-vault', // Always visible
    },
  ];

  // Helper to check if an item should be visible
  const isItemVisible = (item: NavItem): boolean => {
    // Expert mode check
    if (item.expertOnly && userMode !== 'expert') {
      return false;
    }
    // Legacy page visibility check (for crypto.com and kucoin)
    if (item.visibilityKey && !pageVisibility[item.visibilityKey]) {
      return false;
    }
    // Module visibility check (if moduleId is specified)
    if (item.moduleId && !isModuleEnabled(item.moduleId)) {
      return false;
    }
    return true;
  };

  // Filter navigation items and their children
  const navItems = allNavItems
    .map(item => {
      // Filter children if they exist
      if (item.children) {
        const visibleChildren = item.children.filter(child => isItemVisible(child));
        return { ...item, children: visibleChildren };
      }
      return item;
    })
    .filter(item => {
      // If item has children, show only if at least one child is visible
      if (item.children) {
        return item.children.length > 0;
      }
      // Otherwise apply normal visibility check
      return isItemVisible(item);
    });

  const bottomNavItems: NavItem[] = [
    {
      id: 'projects',
      label: 'Projets',
      icon: Icons.Rocket,
      isNew: true,
      onClick: () => {
        onTabChange('projects');
        if (onMobileClose) {
          onMobileClose();
        }
      },
    },
    // {
    //   id: 'recommendations',
    //   label: 'Recommandations',
    //   icon: Icons.Star,
    //   isNew: true,
    //   onClick: () => {
    //     onTabChange('recommendations');
    //     if (onMobileClose) {
    //       onMobileClose();
    //     }
    //   },
    // },
    {
      id: 'account',
      label: 'Mon Compte',
      icon: Icons.User,
      onClick: () => {
        onTabChange('account');
        if (onMobileClose) {
          onMobileClose();
        }
      },
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Icons.Settings,
      onClick: () => {
        onTabChange('settings');
        if (onMobileClose) {
          onMobileClose();
        }
      },
    },
  ];

  const handleItemClick = (itemId: string, hasChildren: boolean) => {
    if (hasChildren) {
      toggleMenu(itemId);
    } else {
      onTabChange(itemId);
      if (onMobileClose) {
        onMobileClose();
      }
    }
  };

  const isChildActive = (item: NavItem): boolean => {
    if (!item.children) return false;
    return item.children.some(child => activeTab === child.id);
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            backdropFilter: 'blur(2px)'
          }}
          onClick={onMobileClose}
        />
      )}

      <aside
        className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'open' : ''}`}
        onMouseEnter={() => !isMobile && setIsCollapsed(false)}
        onMouseLeave={() => !isMobile && setIsCollapsed(true)}
        // V2.I.12 : drawer ARIA attributes when open on mobile.
        role={isMobile && isMobileOpen ? 'dialog' : 'navigation'}
        aria-modal={isMobile && isMobileOpen ? 'true' : undefined}
        aria-label={t('sidebar.navLandmark', 'Navigation principale')}
      >
        {/* Header avec logo - Click to go to Home */}
        <div
          className="sidebar-header"
          onClick={() => {
            onTabChange('home');
            if (onMobileClose) onMobileClose();
          }}
          style={{ cursor: 'pointer' }}
          title="Go to Home"
        >
          {!isCollapsed && (
            <>
              <div className="sidebar-logo">
                <AnimatedLogo size={20} variant="draw-dot" color="white" glowColor="#a78bfa" duration={2} />
              </div>
              <span className="sidebar-brand">Edouard.ai</span>
            </>
          )}
          {isCollapsed && (
            <div className="sidebar-logo">
              <AnimatedLogo size={20} variant="draw-dot" color="white" glowColor="#a78bfa" duration={2} />
            </div>
          )}
        </div>

        {/* Navigation principale */}
        <nav className="sidebar-nav">
          <div className="nav-section">
            {!isCollapsed && (
              <div className="nav-section-title">{t('sidebar.main')}</div>
            )}
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id || isChildActive(item);
              const hasChildren = item.children && item.children.length > 0;
              const isExpanded = expandedMenus.includes(item.id);

              const isHovered = hoveredMenu === item.id;
              const shouldShowSubmenu = isExpanded || isHovered;

              return (
                <div
                  key={item.id}
                  onMouseEnter={() => hasChildren && setHoveredMenu(item.id)}
                  onMouseLeave={() => setHoveredMenu(null)}
                >
                  <button
                    onClick={() => handleItemClick(item.id, !!hasChildren)}
                    className={`nav-item ${isActive ? 'active' : ''} ${hasChildren ? 'has-children' : ''} ${isExpanded ? 'expanded' : ''}`}
                    title={isCollapsed ? getNavLabel(item.id) : undefined}
                    aria-current={isActive ? 'page' : undefined}
                    style={{ position: 'relative' }}
                  >
                    <Icon size={20} />
                    {!isCollapsed && (
                      <>
                        <span className="nav-label">{getNavLabel(item.id)}</span>
                        {item.isNew && (
                          <span style={{ marginLeft: 'auto', marginRight: hasChildren ? '24px' : '0' }}>
                            <NewBadge size="tiny" />
                          </span>
                        )}
                        {item.badge !== undefined && (
                          <span className="nav-badge">{item.badge}</span>
                        )}
                        {hasChildren && (
                          <Icons.ChevronRight
                            size={16}
                            className="nav-chevron"
                            style={{
                              position: 'absolute',
                              right: '12px',
                              transition: 'transform 0.2s ease',
                              transform: shouldShowSubmenu ? 'rotate(90deg)' : 'rotate(0deg)',
                              opacity: 0.6
                            }}
                          />
                        )}
                      </>
                    )}
                  </button>

                  {/* Sub-items - Shown when expanded OR hovered (not collapsed) */}
                  {hasChildren && !isCollapsed && (
                    <div
                      className={`nav-submenu ${shouldShowSubmenu ? 'expanded' : ''}`}
                      style={{
                        maxHeight: shouldShowSubmenu ? `${item.children!.length * 52}px` : '0',
                        overflow: 'hidden',
                        transition: 'max-height 0.3s ease'
                      }}
                    >
                      {item.children!.map((child) => {
                        const ChildIcon = child.icon;
                        const isChildActive = activeTab === child.id;

                        return (
                          <button
                            key={child.id}
                            onClick={() => handleItemClick(child.id, false)}
                            className={`nav-item sub-item ${isChildActive ? 'active' : ''}`}
                            aria-current={isChildActive ? 'page' : undefined}
                            style={{
                              paddingLeft: '40px',
                              fontSize: '13px',
                              position: 'relative'
                            }}
                          >
                            <span
                              style={{
                                position: 'absolute',
                                left: '24px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: '4px',
                                height: '4px',
                                borderRadius: '50%',
                                background: isChildActive ? 'var(--primary)' : 'var(--muted)'
                              }}
                            />
                            <ChildIcon size={16} />
                            <span className="nav-label">{getNavLabel(child.id)}</span>
                            {child.isNew && (
                              <span style={{ marginLeft: 'auto' }}>
                                <NewBadge size="tiny" />
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Sub-items - Collapsed view: only show if this menu has an active child */}
                  {hasChildren && isCollapsed && isChildActive(item) && (
                    <div className="nav-submenu-collapsed">
                      {item.children!.map((child) => {
                        const ChildIcon = child.icon;
                        const isThisChildActive = activeTab === child.id;

                        return (
                          <button
                            key={child.id}
                            onClick={() => handleItemClick(child.id, false)}
                            className={`nav-item sub-item-collapsed ${isThisChildActive ? 'active' : ''}`}
                            title={getNavLabel(child.id)}
                            style={{
                              background: 'transparent',
                              minHeight: '40px',
                              justifyContent: 'center',
                              opacity: isThisChildActive ? 1 : 0.5,
                            }}
                          >
                            <ChildIcon size={16} />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        {/* Footer avec settings et collapse */}
        <div className="sidebar-footer">
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={item.onClick}
                className={`nav-item ${isActive ? 'active' : ''}`}
                title={isCollapsed ? getNavLabel(item.id) : undefined}
              >
                <Icon size={20} />
                {!isCollapsed && (
                  <>
                    <span className="nav-label">{getNavLabel(item.id)}</span>
                    {item.isNew && (
                      <span style={{ marginLeft: 'auto' }}>
                        <NewBadge size="tiny" />
                      </span>
                    )}
                  </>
                )}
              </button>
            );
          })}

          {/* Collapse button - Hidden on mobile */}
          {!isMobile && (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="nav-item collapse-btn"
              title={isCollapsed ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')}
            >
              {isCollapsed ? <Icons.ChevronRight size={20} /> : <Icons.ChevronLeft size={20} />}
            </button>
          )}
        </div>
      </aside>
    </>
  );
}
